(ns frontend.view.components.select
  (:require
   ["react-select" :default Select :refer (components)]
   ["react-select/async-creatable" :default AsyncCreatableSelect]
   ["react-select/async" :default AsyncSelect]
   ["react-select/creatable" :default CreatableSelect]
   [clojure.set :as set]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.entity-types :as entity-types]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.dom-utils :as dom-utils]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.workaround :as workaround]
   [frontend.zap-time :as zap-time]
   [potpuri.core :as potpuri]
   [reagent.core :as r]
   [reagent.impl.component :as reagent-component]))

(def Async-Select AsyncSelect)
(def Async-Select-Creatable AsyncCreatableSelect)

(defn resolve-key
  [o]
  (or (when (keyword? o)
        o)
      (:key o)
      (:value o)
      (:db/id o)
      (if (map? o)
        (do (when-not (:allow-nil? o)
              (logging/error "Select : Cannot resolve option " o))
            nil)
        (when (and (coll? o) (= 2 (count o)))
          (first o)))))

(defn resolve-label
  [o entities-atom]
  (or (when (keyword? o)
        (loc/tr o))
      (:label o)
      (when (:db/id o)
        (or (entity/resolve-entity-name o @entities-atom session/reverse-lookup)
            (entity/display-string o)))
      (when (and (coll? o) (= 2 (count o)) (second o)) (second o))
      (get o "label")))

(defn filter-person-option
  [{include-disabled?          :include-disabled?
    query-attributes           :query-attributes
    query-mandatory-attributes :query-mandatory-attributes
    :or                        {include-disabled? false                                             ; not needed but here to emphasize not included by default
                                }
    :as                        args}]
  (fn [{:keys [search-str-lc entity-candidate]}]
    (when
     (and (or include-disabled?
              (and (not (:common/disabled? entity-candidate))
                   (not (:common/archived? entity-candidate))
                   ;; no need to filter pipeline-specific archive since this filter is for persons only
                   ))
          (or (not query-mandatory-attributes)
              (->> query-mandatory-attributes
                   (remove (fn [a]
                             (get entity-candidate a)))                                             ;; check that mandatory attributes are filled ;; just a nil check - we may want empty string etc checking as well
                   (empty?))))
      (let [searchable-values (or (when-not query-attributes
                                    [(or (entity/resolve-entity-name entity-candidate
                                                                     session/get-entity
                                                                     session/reverse-lookup)
                                         (entity/display-string entity-candidate))])
                                  (->> query-attributes
                                       (map (partial get entity-candidate))))]
        (->> searchable-values
             (map cu/str-trim-lcase)
             (some #(string/includes? % search-str-lc)))))))

(defn get-option-value
  [{:keys [options value-type] :as args} option]
  (let [option (js->clj option)
        v (get option "value")
        new-value (or (get option "new-value")
                      (get option :new-value))
        res (cond
              (some? v) v
              (and (coll? option) (= (count option) 2)) (first option)
              (string? option) option
              (number? option) option
              (keyword? option) option
              (char? option) option
              (:value option) (:value option)
              (:key option) (:key option)
              (:db/id option) (:db/id option)
              (get option "value") (get option "value")
              (get option "key") (get option "key")
              (get option "db/id") (get option "db/id")
              :else (some->> options
                             (filter (fn [o]
                                       (= o option)))
                             (first)))
        coerced (case value-type
                  :entity (let [id (js/parseInt (str res))]
                            (when-not (js/isNaN id)
                              id))
                  :string (str res)
                  :number (let [n (js/parseInt (str res))]
                            (when-not (js/isNaN n)
                              n))
                  :keyword (if (keyword? res) res (keyword (str res)))
                  res)]
    (when (and (not coerced)
               (not new-value)
               (not (and (coll? option)
                         (or (= "new-value" (first res))
                             (= :new-value (first res))))))
      (logging/error "Could not resolve select/get-option-value "
                     res
                     "  \n"
                     option
                     "\n"
                     (when option args)
                     "\n"
                     option
                     "]"))
    coerced))

(defn get-option-label
  [{:keys [is-multi has-focus? temp-atom entities-atom
           entity-types-atom search-function value-type entity-types
           named-value-attribute options value filter-fn max-results]
    :as   args} js-option & rest]
  (let [option (js->clj js-option)
        matching-option (some->> options
                                 (filter (fn [opt]
                                           (or (= (:key opt) option)
                                               (= (:value opt) option))))
                                 (first))
        option-key (cond
                     matching-option (:key matching-option)
                     (string? option) option
                     (number? option) option
                     (and (map? option)
                          (get option "db/id"))
                     (let [res (js/parseInt (str (get option "db/id")))]
                       (if (js/isNaN res)
                         nil
                         res))
                     (map? option) (or (get option "key")
                                       (get option "value"))
                     (and (coll? option) (= 2 (count option))) (first option)
                     :else (str option))
        comparable-value (case value-type
                           :keyword (keyword (cond (and (string? option-key)
                                                        (string/starts-with? option-key ":"))
                                                   (subs option-key 1)
                                                   :else option-key))
                           :number (let [res (if (number? option-key)
                                               option-key
                                               (js/parseInt (str option-key)))]
                                     (if (js/isNaN res)
                                       nil
                                       res))
                           :string (str option-key)
                           :entity (if (number? option-key)
                                     option-key
                                     (let [res (js/parseInt (str option-key))]
                                       (if (js/isNaN res)
                                         nil
                                         res)))
                           (str option-key))
        res
        (cond
          matching-option (:label matching-option)
          (and (map? option)
               (get option "label"))
          (get option "label")
          (:label option) (:label option)
          (and (= value-type :entity)
               comparable-value
               entities-atom
               (get @entities-atom comparable-value))
          (let [entity (get @entities-atom comparable-value)]
            (or (entity/resolve-entity-name entity @entities-atom session/reverse-lookup)
                (entity/display-string entity)))
          (and (coll? option) (= 2 (count option)) (second option))
          (str (second option))
          (and options)
          (->>
           options
           (keep
            (fn [o]
              (let [option-val (resolve-key o)
                    coerced-val
                    (case value-type
                      :keyword (if (keyword? option-val) option-val (keyword (str option-val)))
                      :number (if (number? option-val) option-val (js/parseInt (str option-val)))
                      :entity (if (number? option-val) option-val (js/parseInt (str option-val)))
                      :string (str option-val)
                      (str option-val))]
                (when (= comparable-value coerced-val)
                  (resolve-label o entities-atom)))))
           (first)
           (str))
          :else (str comparable-value))]
    res))

(defn entity-search-string-lc
  [entity]
  (let [entity-string-lc (or (when-not (contains? #{:entity.type/person
                                                    :entity.type/account
                                                    :entity.type/fund-commitment}
                                                  (:entity/type entity))
                               (:common/lower-case-str entity))
                             (some-> entity
                                     fu/resolve-name
                                     cu/str-trim-lcase)
                             (some-> entity
                                     entity/display-string
                                     cu/str-trim-lcase))]
    entity-string-lc))

(defn starts-with-sort
  [lcase-search a b]
  (let [a-label (cu/str-trim-lcase (second a))
        b-label (cu/str-trim-lcase (second b))]
    (if (empty? lcase-search)
      (compare a-label b-label)
      (let [a-starts? (string/starts-with? a-label lcase-search)
            b-starts? (string/starts-with? b-label lcase-search)]
        (cond (and a-starts? b-starts?) (compare a-label b-label)
              a-starts? -1
              b-starts? 1
              :else (compare a-label b-label))))))

(defn default-sort
  [by-ord? lcase-search id-name-values]
  (let [sort-fn (partial starts-with-sort lcase-search)]
    (if by-ord?
      (->> id-name-values
           (sort-by (fn [[id display-string]]
                      (-> (get @session/entities id)
                          :common/ord))))
      (->> id-name-values
           (sort sort-fn)))))

(defn filter-indexed-results
  [{:keys [search-str-lc has-focus? entity-types-atom filter-fn containing-entity]} results]
  (let [pipeline-id (pipeline/entity-pipeline containing-entity)
        results
        (->> results
             (remove (fn [[entity-id _display-string]]
                       (let [entity (session/get-entity entity-id)]
                         (or (:common/archived? entity)
                             (and (= :entity.type/named-value (:entity/type entity))
                                  (some #{pipeline-id} (:named-value/archived-in-pipelines entity)))
                             (:common/disabled? entity))))))]
    (if-not (fn? filter-fn)
      results
      (->> results
           (filter (fn [[entity-id display-string]]
                     (let [entity (session/get-entity entity-id)]
                       (filter-fn {:search-str-lc         search-str-lc
                                   :containing-entity     containing-entity
                                   :has-feature-access-fn session/has-feature-access?
                                   :has-focus?            has-focus?
                                   :search-fn?            identity
                                   :entity-candidate      entity
                                   :entity-types-lookup   @entity-types-atom}))))))))

(defn explicit-options
  [{:keys [options filter-fn custom-sort? search-str-lc containing-entity
           has-focus? sort-fn entity-types-atom]}]
  (let [options (->> options
                     (remove :common/disabled?))
        options (if-not (fn? filter-fn)
                  (->> options
                       (keep (fn [o]
                               (let [k (resolve-key o)
                                     v (resolve-label o session/entities)]
                                 (when-not (or k (:allow-nil? o))
                                   (logging/error "Could not load options " o " << " options))
                                 (when (or (not has-focus?)
                                           (and (or k (:allow-nil? o))
                                                v
                                                (or (string/includes? (string/lower-case (str v))
                                                                      search-str-lc)
                                                    (string/includes? (string/lower-case (str k))
                                                                      search-str-lc))))
                                   [k v])))))
                  (->> options
                       (filter (fn [o]
                                 (filter-fn {:search-str-lc         search-str-lc
                                             :containing-entity     containing-entity
                                             :has-feature-access-fn session/has-feature-access?
                                             :has-focus?            has-focus?
                                             :search-fn?            identity
                                             :entity-candidate      o
                                             :entity-types-lookup   @entity-types-atom})))
                       (map (fn [o]
                              (let [k (resolve-key o)
                                    v (resolve-label o session/entities)]
                                (when-not (or k (:allow-nil? o))
                                  (logging/error "Could not load options " o " << " options))
                                [k v])))))]
    (if custom-sort?
      options
      (->> options
           (sort sort-fn)))))

(defn get-options
  [{:keys [options
           has-focus?
           containing-entity
           value-type
           entity-types
           field/custom-sort
           entity-types-atom
           named-value-attribute
           cached-options
           filter-fn
           match-any-word?
           search-str-lc]
    :as   props}]
  (let [sort-fn (partial starts-with-sort search-str-lc)
        pipeline-id (pipeline/entity-pipeline containing-entity)
        tenant-schema @session/tenant-schema
        custom-sort? (or custom-sort
                         (some->> named-value-attribute
                                  (registry/get-field-schema tenant-schema)
                                  :create-on-fly?)
                         (some->> entity-types
                                  (some (fn [et]
                                          (->> entity-types/entities
                                               et
                                               ;; TODO: what does custom sort mean - do we really need this ???
                                               :entity/custom-sort)))))
        result
        (cond
          options
          (explicit-options
           {:options           options
            :filter-fn         filter-fn
            :custom-sort?      custom-sort?
            :search-str-lc     search-str-lc
            :containing-entity containing-entity
            :has-focus?        has-focus?
            :sort-fn           sort-fn
            :entity-types-atom entity-types-atom})

          (and (= value-type :entity)
               (coll? entity-types)
               (seq entity-types)
               entity-types-atom)
          (do
            #_(logging/debug "Select get-options << entity-type non-indexed" entity-types)
            (->>
             entity-types
             (mapcat
              (fn [et]
                (some->>
                 entity-types-atom
                 (deref)
                 et
                 (remove :common/disabled?)
                 (remove
                  (fn [e]
                    (case (:entity/type e)
                      :entity.type/stage
                      (fu/stage-in-pipeline?
                       {:pipeline-stage             (:stage/pipeline e)
                        :containing-entity-pipeline (:oppo/pipeline containing-entity)
                        :containing-entity          containing-entity
                        :selected-pipeline          @session/selected-oppo-pipeline})
                      :entity.type/opportunity-stage
                      (fu/stage-in-pipeline?
                       {:pipeline-stage             (:opportunity-stage/pipeline e)
                        :containing-entity-pipeline (:fundraising-opportunity/pipeline
                                                     containing-entity)
                        :containing-entity          containing-entity
                        :selected-pipeline          @session/selected-froppo-pipeline})
                      :entity.type/sales-opportunity-stage
                      (fu/stage-in-pipeline?
                       {:pipeline-stage             (:sales-opportunity-stage/pipeline e)
                        :containing-entity-pipeline (:sales-opportunity/pipeline
                                                     containing-entity)
                        :containing-entity          containing-entity
                        :selected-pipeline          @session/selected-sales-oppo-pipeline})
                      :entity.type/exit-opportunity-stage
                      (fu/stage-in-pipeline?
                       {:pipeline-stage             (:exit-opportunity-stage/pipeline e)
                        :containing-entity-pipeline (:exit-opportunity/pipeline
                                                     containing-entity)
                        :containing-entity          containing-entity
                        :selected-pipeline          @session/selected-exit-oppo-pipeline})
                      false)))
                 (map
                  (fn [e]
                    (let [search-fn?
                          (fn [l-case-entity-str]
                            (or (not has-focus?)
                                (empty? search-str-lc)
                                (and (string? l-case-entity-str)
                                     (string/includes? l-case-entity-str search-str-lc))
                                (and match-any-word?
                                     (when l-case-entity-str
                                       (let [parts (string/split l-case-entity-str #"\s")]
                                         (->> parts
                                              (some (fn [part]
                                                      (string/includes? part
                                                                        search-str-lc)))))))))]
                      (when
                       (and (not (:common/archived? e))
                            (or (not pipeline-id)
                                (not= :entity.type/named-value et)
                                (not-any? #{pipeline-id}
                                          (:named-value/archived-in-pipelines e)))
                            (not (and (= et :entity.type/account)
                                      (= (:account/first-name e) "system")))
                            (or (not= et :entity.type/named-value)
                                (= (:named-value/attribute e) named-value-attribute))
                            (if filter-fn
                              (filter-fn {:search-str-lc         search-str-lc
                                          :containing-entity     containing-entity
                                          :has-feature-access-fn session/has-feature-access?
                                          :has-focus?            has-focus?
                                          :search-fn?            search-fn?
                                          :entity-candidate      e
                                          :entity-types-lookup   @entity-types-atom})
                              (search-fn? (entity-search-string-lc e))))
                        [(:db/id e)
                         (or
                          (entity/resolve-entity-name e session/get-entity session/reverse-lookup)
                          (entity/display-string e))])))))))
             (remove nil?)
             (default-sort custom-sort? search-str-lc))))]
    result))

(defn load-options
  [{:keys [temp-atom search-function] :as args} input-str-lc callback]
  ;; TODO: filter options based on input-str
  (let [timeout-handle
        (->> 600
             (js/setTimeout
              (fn []
                (let [start (zap-time/now)
                      push-results (fn [results]
                                     (callback (or results []))
                                     (let [time-in-ms (- (zap-time/now) start)]
                                       (when (and (> time-in-ms 50)
                                                  (session/is-admin?))
                                         (logging/performance "load-options is too slow : "
                                                              (- (zap-time/now) start)
                                                              " -> " (first results)
                                                              "\n" args))))]
                  (search-function input-str-lc push-results)))))]
    (when temp-atom
      (when-let [load-timeout-handle (:timeout-handle @temp-atom)]
        (js/clearTimeout load-timeout-handle))
      (swap! temp-atom assoc :timeout-handle timeout-handle))))

(defn render-option
  [select-val props]
  (let [{:keys [isDisabled
                value
                isSelected
                isFocused
                label
                isRtl
                data]}
        props
        getStyles (:getStyles props)
        selected? (or isSelected
                      (and (coll? select-val)
                           (some #{value} select-val))
                      (= select-val value))
        kept-inner-props (dissoc (:innerProps (js->clj props))
                                 "onMouseMove"
                                 "onMouseOver")]
    [:div
     (merge
      {:class-name (fu/cs :react-select__option
                          (when selected?
                            :is-selected)
                          (when isFocused
                            :is-focused)
                          (when-let [cn (:class-name data)]
                            cn))}
      kept-inner-props)
     (when-let [icon-fn (:icon data)]
       (icon-fn data))
     label]))

(defn row-renderer
  [select-atom props js-args]
  (let [{:keys [cache value-type entities-atom on-change set-value! on-create-option is-multi multi-selectable?
                select!]} props
        args (js->clj js-args :keywordize-keys true)
        {:keys [is-visible column-index key parent row-index style index]} args
        options (cond (and is-multi multi-selectable?)
                      (:all-options @select-atom)
                      (and is-multi (not multi-selectable?))
                      (:unselected-options @select-atom)
                      :else
                      (:all-options @select-atom))
        render-value (:render-value @select-atom)
        current-value-set (if (coll? render-value)
                            (set render-value)
                            (set [render-value]))
        option (nth options index)
        highlight? (= index (:highlighted-index @select-atom))
        option-key (cond
                     (string? option) option
                     (number? option) option
                     (and (map? option)
                          (get option "db/id"))
                     (let [res (js/parseInt (str (get option "db/id")))]
                       (if (js/isNaN res)
                         nil
                         res))
                     (map? option) (or (get option "key")
                                       (get option "value"))
                     (:key option) (:key option)
                     (and (coll? option) (= 2 (count option))) (first option)
                     :else (str option))
        selected? (some #{option-key} current-value-set)
        comparable-value (case value-type
                           :keyword (keyword (cond (and (string? option-key)
                                                        (string/starts-with? option-key ":"))
                                                   (subs option-key 1)
                                                   :else option-key))
                           :number (let [res (if (number? option-key)
                                               option-key
                                               (js/parseInt (str option-key)))]
                                     (if (js/isNaN res)
                                       nil
                                       res))
                           :string (str option-key)
                           :entity (if (number? option-key)
                                     option-key
                                     (let [res (js/parseInt (str option-key))]
                                       (if (js/isNaN res)
                                         nil
                                         res)))
                           (str option-key))
        label
        (cond
          (and (map? option)
               (get option "label"))
          (get option "label")
          (:label option) (:label option)
          (and (= value-type :entity)
               comparable-value
               entities-atom
               (get @entities-atom comparable-value))
          (let [entity (get @entities-atom comparable-value)]
            (or (and (coll? option)
                     (= 2 (count option))
                     (second option))
                (entity/resolve-entity-name entity @entities-atom session/reverse-lookup)
                (entity/display-string entity)))
          (and (coll? option) (= 2 (count option)) (second option))
          (str (second option))
          (and options)
          (->>
           options
           (keep
            (fn [o]
              (let [option-val (resolve-key o)
                    coerced-val
                    (case value-type
                      :keyword (if (keyword? option-val) option-val (keyword (str option-val)))
                      :number (if (number? option-val) option-val (js/parseInt (str option-val)))
                      :entity (if (number? option-val) option-val (js/parseInt (str option-val)))
                      :string (str option-val)
                      (str option-val))]
                (when (= comparable-value coerced-val)
                  (resolve-label o entities-atom)))))
           (first)
           (str))
          :else (str comparable-value))]
    (r/as-element
     [:> js/ReactVirtualized.CellMeasurer
      {:cache        cache
       :column-index 0
       :key          key
       :style        style
       :parent       parent
       :row-index    index}
      [:div
       {:style           style
        :class           (fu/cs :zap-select-virtualized-dropdown-menu-option
                                (when multi-selectable?
                                  :multi-selectable)
                                (when-let [cn (:class-name option)]
                                  cn)
                                (when highlight?
                                  :is-focused)
                                (when selected?
                                  :is-selected)
                                (when highlight?
                                  :highlight))
        :on-mouse-down   (fn [e]
                           (swap! select-atom assoc
                                  :highlighted-index
                                  index)
                           (js/setTimeout (fn [_]
                                            (select! index))
                                          10))
        :on-double-click (fn [e]
                           ;;(toggle-expand!)
                           )}
       (if-not multi-selectable?
         [:span
          (when-let [icon-fn (:icon option)]
            (icon-fn option))
          (str label)]
         [:label.zap-inlined-checkbox-label
          [:input
           {:type       "checkbox"
            :checked    (boolean selected?)
            :on-change  (fn [_])
            :class-name (fu/cs "component-checkbox")}]
          [:span.checkmark]
          [:span.checkbox-label (str label)]])]])))

(defn render-dropdown
  [component-args props]
  (let [state (r/atom {})
        {:keys [value-type value entities-atom on-change max-opts-visible
                on-create-option select-atom multi-selectable? force-opts-height?]}
        component-args
        cache (js/ReactVirtualized.CellMeasurerCache.
               #js
                       {:defaultHeight 150
                        ;;:min-height 100
                        :fixed-width   true
                        :fixedWidth    true
                        ;;"fixedWidth" true
                        })
        {:keys [selectOption getStyles getValue menuPlacement
                innerRef setValue menuPosition menuShouldScrollIntoView
                theme isLoading selectProps cx hasValue maxMenuHeight options
                clearValue placement innerProps isMulti minMenuHeight]}
        props
        control-height (aget (aget theme "spacing") "controlHeight")
        select! (fn [index]
                  (let [render-value (:render-value @select-atom)
                        options (cond (and isMulti multi-selectable?)
                                      (:all-options @select-atom)
                                      (and isMulti (not multi-selectable?))
                                      (:unselected-options @select-atom)
                                      :else
                                      (:all-options @select-atom))
                        render-value (:render-value @select-atom)
                        current-value-set (if (coll? render-value)
                                            (set render-value)
                                            (set [render-value]))
                        option (nth options index)
                        option-key (cond
                                     (string? option) option
                                     (number? option) option
                                     (and (map? option)
                                          (get option "db/id"))
                                     (let [res (js/parseInt (str (get option "db/id")))]
                                       (if (js/isNaN res)
                                         nil
                                         res))
                                     (map? option) (or (get option "key")
                                                       (get option "value"))
                                     (:key option) (:key option)
                                     (and (coll? option) (= 2 (count option))) (first option)
                                     :else (str option))
                        selected? (some #{option-key} current-value-set)

                        current-value-set (if (coll? render-value)
                                            (set render-value)
                                            (set [render-value]))
                        new-value (cond (and isMulti (not selected?))
                                        (set/union current-value-set #{option-key})
                                        (and isMulti selected?)
                                        (set/difference current-value-set #{option-key})
                                        :else
                                        option-key)]
                    (if (:new-value option)
                      (do
                        (on-create-option (:new-value option))
                        (swap! select-atom dissoc
                               :menu-is-open?
                               :temp-input)
                        (swap! select-atom assoc :has-focus? false)
                        (deref select-atom))
                      (setValue new-value {}))))]
    (swap! select-atom assoc :highlight-select! select!)
    (fn [component-args props]
      [:> js/ReactVirtualized.AutoSizer
       (fn [m]
         (let [autosizer-width (aget m "width")
               all-options (:all-options @select-atom)
               rendered-options (cond (and isMulti multi-selectable?)
                                      all-options
                                      (and isMulti (not multi-selectable?))
                                      (:unselected-options @select-atom)
                                      :else
                                      all-options)
               highlighted-index (:highlighted-index @select-atom)
               option-count (count rendered-options)
               default-height (fn []
                                (apply min
                                       (concat [(/ (+ minMenuHeight maxMenuHeight) 2)
                                                (* 30 option-count)]
                                               (when max-opts-visible
                                                 [(* 30 max-opts-visible)]))))
               ;;hack-ish way of displaying all options of new-kpi-cycle modal's year-selector.
               ;; note: calculations above might also require 31 instead of 30.
               ;; requires more investigation & more general solution.
               opts-height (fn []
                             ;; '- 9' to prevent extra scrollbar - might be specific to new-kpi-cycle-modal:
                             (apply min
                                    (concat [(- maxMenuHeight 9)
                                             (* 31 option-count)]
                                            (when max-opts-visible
                                              [(* 31 max-opts-visible)]))))
               height (if force-opts-height?
                        (opts-height)
                        (default-height))]
           (r/as-element
            (into
             [:div
              {:on-mouse-down (fn [e]
                                (let [previous-timeout (get @select-atom :timeout)]
                                  (when previous-timeout
                                    (js/clearTimeout previous-timeout))
                                  (swap! select-atom assoc
                                         :cannot-close-menu? true
                                         :has-focus? true
                                         :timeout (js/setTimeout
                                                   (fn []
                                                     (swap! select-atom assoc :cannot-close-menu? false))
                                                   500))))}
              [:> js/ReactVirtualized.List
               (merge {:class-name                 (fu/cs :zap-select-virtualized-dropdown-menu placement)
                       :row-count                  option-count
                       :innerProps                 innerProps
                       :height                     height
                       :width                      autosizer-width
                       :scrollToIndex              highlighted-index
                       :onScrollbarPresenceChange
                       (fn [js-args]
                         (let [{:keys [vertical]} (js->clj js-args :keywordize-keys true)]
                           (swap! state assoc :vertical-scrollbar? vertical)))
                       :deferred-measurement-cache cache
                       :row-width                  autosizer-width
                       :row-height                 (.-rowHeight cache)
                       :row-renderer               (partial row-renderer
                                                            select-atom
                                                            {:cache             cache
                                                             :entities-atom     entities-atom
                                                             :on-create-option  on-create-option
                                                             :set-value!        setValue
                                                             :select!           select!
                                                             :list-state        state
                                                             :on-change         on-change
                                                             :select-atom       select-atom
                                                             :is-multi          isMulti
                                                             :multi-selectable? multi-selectable?
                                                             :value-type        value-type})
                       :ref                        (fn [component]
                                                     ;;(when (and component
                                                     ;;           (not (= component (get-in @db/lists [list-id :list-component]))))
                                                     (swap! state assoc :list-component component))}
                      (when (= placement "top")
                        {:style {:bottom (str (+ (min (/ (+ minMenuHeight maxMenuHeight) 2)
                                                      (* 30 option-count))
                                                 (if (number? control-height)
                                                   control-height
                                                   30))
                                              "px")}}))]]))))])))

(defn recalc-options
  [{:keys [select-atom props value is-multi all-options cache-updated update-option-list]} callback]
  (let [search-str-lc (cu/str-trim-lcase (if (:has-focus? @select-atom)
                                           (:temp-input @select-atom)
                                           (or (:temp-input @select-atom)
                                               (get-option-label props value)
                                               value)))
        current-value-set (set (if is-multi
                                 value
                                 [value]))]
    (if-let [load-options (:load-select-options props)]
      (load-options search-str-lc
                    (fn [loaded-options]
                      (update-option-list current-value-set loaded-options callback)))
      (let [options (get-options
                     (merge props
                            {:search-str-lc          search-str-lc
                             :cached-options         all-options
                             :cached-options-created cache-updated
                             :has-focus?             true}))]
        (update-option-list current-value-set options callback)))))

(defn attach-scroll-handler-to-parents
  [node handler]
  (when node
    (.addEventListener node "scroll" handler)
    (recur (-> node
               .-parentElement)
           handler)))

(defn remove-scroll-handler-to-parents
  [node handler]
  (when node
    (.removeEventListener node "scroll" handler)
    (recur (-> node
               .-parentElement)
           handler)))

(defn create-select
  [args]
  (let [props (:props args)
        {:keys [is-multi multi-selectable? attribute value read-only? on-change! class-name
                on-menu-close! on-menu-open! close-menu-on-scroll?
                open-on-start? hide-dropdown-indicator? style-override]} props
        select-atom (r/atom {:menu-is-open? open-on-start?})
        timeout-atom (atom {})
        cache-updated (zap-time/now)
        all-options (get-options
                     (merge {:entity-types-atom session/entity-types}
                            props
                            {:search-str-lc ""
                             :has-focus?    true}))
        menu-closer (fn [e]
                      (let [dom-elem (:dom-node @select-atom)
                            target (some->> e .-target)]
                        (if
                         (or (and dom-elem
                                  (dom-utils/is-ancestor? target dom-elem))
                             (when-let [menu-portal-target (:menu-portal-target args)]
                               (dom-utils/is-ancestor? target menu-portal-target)))
                          (do
                            (swap! select-atom assoc :cannot-close-menu? true))
                          (do
                            (let [closer! (:close-menu! @select-atom)]
                              (closer! e))))))
        close-menu! (fn [e]
                      (if (:cannot-close-menu? @select-atom)
                        (js/setTimeout #(swap! select-atom dissoc :cannot-close-menu?) 50)
                        (do
                          (swap! select-atom dissoc :temp-input)
                          (swap! select-atom assoc :menu-is-open? false)
                          (.removeEventListener js/document "mousedown" menu-closer))))
        open-menu! (fn [e]
                     (.addEventListener js/document "mousedown" menu-closer))
        _ (swap! select-atom assoc :close-menu! close-menu!)

        backspace-on-empty-multi?
        (fn [selected-option js-params]
          (let [matching-selected-option []
                matching-js-params {:action "pop-value" :removedValue nil :name nil}]
            (and is-multi
                 (= matching-selected-option (js->clj selected-option))
                 (= matching-js-params (js->clj js-params :keywordize-keys true)))))
        prevent-on-change? (fn [selected-option js-params]
                             (backspace-on-empty-multi? selected-option js-params))
        on-select-change (fn [selected-option js-params]
                           (let [new-value selected-option]
                             ;; (single-select and multi-selectable handled with :backspace-removes-value)
                             ;; backspace on empty input will trigger this - preventing explicitly:
                             ;; (note: ensure this still works whenever updating react-select version)
                             (when-not (prevent-on-change? new-value js-params)
                               (let [params (js->clj js-params :keywordize-keys true)
                                     removed-value (:removedValue params)
                                     k (if is-multi
                                         (->> new-value
                                              (map (fn [o]
                                                     (if (and (coll? o) (= (count o) 2))
                                                       (if (= (first o) :new-option)
                                                         {:new-option (second o)}
                                                         (first o))
                                                       o)))
                                              (remove (fn [v]
                                                        (= v removed-value)))
                                              (into []))
                                         new-value)]
                                 (when (fn? on-change!)
                                   (r/after-render (partial on-change! k)))
                                 (when-not multi-selectable?
                                   (swap! select-atom assoc
                                          :menu-is-open? false
                                          :temp-input nil
                                          :cannot-close-menu? false
                                          :has-focus? false)
                                   (close-menu! nil))))))
        menu-component (r/reactify-component (partial render-dropdown
                                                      (merge
                                                       (select-keys props
                                                                    [:value-type
                                                                     :value
                                                                     :max-opts-visible
                                                                     :force-opts-height?
                                                                     :entities-atom
                                                                     :multi-selectable?
                                                                     :on-create-option])
                                                       {:select-atom select-atom})))
        get-new-option-data (:get-new-option-data props)
        is-valid-new-option
        (or (:is-valid-new-option props)
            (when (:creatable? args)
              (fn [args]
                (let [l-str (string/lower-case (str args))
                      ;; TODO : all-options is not recalculated - does it matter ?
                      options all-options
                      #_(get-options (merge props
                                            {:search-str-lc (string/lower-case args)
                                             :has-focus?    true}
                                            (when-not (:entity-types-atom props)
                                              {:entity-types-atom session/entity-types})))
                      filtered-options (->> options
                                            (filter (fn [[k v]]
                                                      (= (-> v
                                                             (str)
                                                             (string/lower-case))
                                                         l-str))))
                      res (and (not-empty (str args))
                               (coll? filtered-options)
                               (empty? filtered-options))]
                  res))))
        update-option-list
        (fn [current-value-set ordered-options callback]
          (let [input-txt (cu/str-trim (:temp-input @select-atom))
                create-opt (when (and is-valid-new-option
                                      get-new-option-data
                                      (is-valid-new-option input-txt))
                             [(get-new-option-data input-txt)])
                all-options (concat create-opt
                                    ordered-options)
                unselected-options
                (->> (concat
                      create-opt
                      (->> ordered-options
                           (keep
                            (fn [option]
                              (let [option-key
                                    (cond
                                      (string? option) option
                                      (number? option) option
                                      (and (map? option)
                                           (get option "db/id"))
                                      (let [res (js/parseInt (str (get option "db/id")))]
                                        (if (js/isNaN res)
                                          nil
                                          res))
                                      (map? option) (or (get option "key")
                                                        (get option "value"))
                                      (:key option) (:key option)
                                      (:value option) (:value option)
                                      (:db/id option) (:db/id option)
                                      (and (coll? option) (= 2 (count option))) (first
                                                                                 option)
                                      :else (str option))]
                                (when-not (some #{option-key} current-value-set)
                                  option))))))
                     (vec))]
            (swap! select-atom assoc
                   :unselected-options unselected-options
                   :all-options all-options)
            (callback all-options)))
        scroll-handler (fn [e]
                         (when (:menu-is-open? @select-atom)
                           (swap! select-atom dissoc :menu-is-open?)))
        attach-scroll-handler (fn [dom-node]
                                (when (and dom-node close-menu-on-scroll?)
                                  (attach-scroll-handler-to-parents dom-node
                                                                    scroll-handler)
                                  (swap! select-atom assoc :dom-node dom-node)))]
    (r/create-class
     {:component-did-mount    (fn [args]
                                (swap! select-atom assoc :render-value value))
      :component-will-unmount (fn [args]
                                (when close-menu-on-scroll?
                                  (when-let [dom-node (:dom-node @select-atom)]
                                    (remove-scroll-handler-to-parents dom-node scroll-handler))))
      :component-did-update   (fn [this old-argv]
                                (let [prev-value (-> old-argv
                                                     reagent-component/extract-props
                                                     :props
                                                     :value)
                                      new-value (-> this
                                                    r/props
                                                    :props
                                                    :value)]
                                  (when-not (= prev-value new-value)
                                    ;;force remount
                                    (swap! select-atom assoc :render-value new-value))))
      :reagent-render
      (fn [args]
        (let [props (:props args)
              original-options (:options props)
              options (when (sequential? original-options)
                        (->> original-options
                             (mapv (fn [o]
                                     (if-not (:label o)
                                       o
                                       (merge o
                                              {:label-lc (->> o
                                                              :label
                                                              (cu/str-trim-lcase))}))))))

              props (merge {:entity-types-atom session/entity-types}
                           props
                           {:options options})
              {:keys [is-multi attribute value read-only? on-focus! auto-focus value-type]} props
              recalculate-options (partial recalc-options
                                           {:select-atom        select-atom
                                            :props              props
                                            :value              value
                                            :is-multi           is-multi
                                            :all-options        all-options
                                            :cache-updated      cache-updated
                                            :update-option-list update-option-list})
              creatable? (:creatable? args)
              value-option (if-not is-multi
                             {:value value
                              :label (get-option-label props value)}
                             (->> value
                                  (map (fn [v]
                                         {:value v
                                          :label (get-option-label props v)}))
                                  (doall)))
              coerced-value (if is-multi
                              (->> value
                                   (map (fn [v]
                                          (workaround/clj->js-workaround v)))
                                   (set))
                              (workaround/clj->js-workaround value-option))
              ;;_ (js/console.log "Select value : " coerced-value " << " value)
              menu-is-open? (boolean (or (:menu-is-open? @select-atom)
                                         (:debug? props)))
              has-focus? (:has-focus? @select-atom)
              empty-value? (fu/empty-value? value)
              value-is-entity? (= :entity value-type)
              value-entity-empty? (when (and value-is-entity?
                                             ;; when options are explicitly passed session/entities should not be used
                                             (empty? original-options))
                                    (if (set? value)
                                      (->> value
                                           (every? (fn [el]
                                                     (nil? (session/get-entity el)))))
                                      (nil? (session/get-entity value))))

              component-args
              (merge
               {:ref                     (fn [ref]
                                           (when ref
                                             (when-let [control-ref (aget ref "controlRef")]
                                               (when (not= control-ref (:ref @select-atom))
                                                 (attach-scroll-handler control-ref)
                                                 (swap! select-atom assoc :ref ref)))))
                ;; Value needs to be passed so that select renders the value
                ;; - we don't want to show the value on single select when the input has focus
                ;; because it looks exactly like input text -> hard to see that it acts as a placeholder
                ;; this we don't pass the value but the value text as placeholder
                ;; also if value is not empty the placeholder is not shown at all
                :value                   (when (and (not empty-value?)
                                                    (or is-multi
                                                        (not has-focus?))
                                                    (not value-entity-empty?))
                                           coerced-value)
                :default-input-value     ""
                :class-name              (fu/cs "react-select" (when is-multi "is-multi") class-name)
                :class-name-prefix       "react-select"
                :is-disabled             (boolean read-only?)
                :isClearable             false
                ;; backspace shouldn't remove value for single-selects or
                ;;  multi-selectable.
                ;; when updating react-select ensure backspace still works as expected
                ;; (see https://github.com/JedWatson/react-select/pull/3216)
                :backspace-removes-value (and is-multi
                                              (not multi-selectable?))
                :auto-blur               (not has-focus?)
                :menu-placement          "auto"
                :blur-input-on-select    true
                :defaultOptions          true
                :onSelectResetsInput     true
                :loadOptions             (fn [input callback]
                                           (swap! select-atom assoc :temp-input input)
                                           (try (recalculate-options (fn [options]
                                                                       (when (fn? callback)

                                                                         (callback (clj->js options))))
                                                                     #_(resolve
                                                                        {:options        options
                                                                         :has-next-page? false}))
                                                (catch js/Object e
                                                  (js/console.log "ERROR : " e))))
                :onBlurResetsInput       true
                :onCloseResetsInput      true
                :no-options-message      (fn [a]
                                           (let [iv (aget a "inputValue")
                                                 iv (string/trim (str iv))]
                                             (if (empty? iv)
                                               (loc/tr :no-options-message-empty)
                                               (loc/tr :no-options-message [iv]))))
                :is-focused              (boolean has-focus?)
                :on-focus                (fn [e]
                                           (swap! select-atom assoc
                                                  :menu-is-open? true
                                                  :has-focus? true
                                                  :temp-input "")
                                           (recalculate-options (fn [_]
                                                                  (swap! select-atom assoc
                                                                         :menu-is-open? true
                                                                         :highlighted-index 0)))
                                           (when on-focus!
                                             (on-focus!)))
                :auto-focus              (boolean auto-focus)
                :on-blur                 (fn [e]
                                           (when-not (:cannot-close-menu? @select-atom)
                                             (swap! select-atom assoc :has-focus? false)))
                :open-menu-on-focus      (when open-on-start?
                                           open-on-start?)
                :menuIsOpen              menu-is-open?
                :on-menu-open            (fn [e]
                                           (swap! select-atom assoc :menu-is-open? true)
                                           (when-not (:menu-is-open? @select-atom)
                                             (recalculate-options (fn [_]
                                                                    (swap! select-atom assoc
                                                                           :menu-is-open? true
                                                                           :highlighted-index 0)
                                                                    (open-menu! e))))
                                           (when (fn? on-menu-open!)
                                             (on-menu-open!)))
                :on-menu-close           (fn [e]
                                           (swap! select-atom dissoc :highlight-select!)
                                           (when-not (:cannot-close-menu? @select-atom)
                                             (close-menu! e))
                                           (when (fn? on-menu-close!)
                                             (on-menu-close!)))
                :on-change               on-select-change}
               {:get-option-value (partial get-option-value props)
                :get-option-label (partial get-option-label props)}
               (when (and creatable? is-valid-new-option)
                 {:is-valid-new-option is-valid-new-option
                  :on-create-option    (fn [opt])})
               (when multi-selectable?
                 {:control-should-render-value false})

               (when (or style-override
                         (:debug-styles props))
                 {:styles (clj->js
                           (merge
                            {}
                            style-override
                            (when (:debug-styles props)
                              {:option (fn [js-styles js-props]
                                         (let [styles (js->clj js-styles)
                                               opt-props (js->clj js-props :keywordize true)
                                               opt-val (get opt-props "value")]
                                           (clj->js
                                            (merge styles
                                                   {"padding" "3px"}
                                                   (when (or (and (coll? value)
                                                                  (some #{opt-val} value))
                                                             (= opt-val value))
                                                     {"backgroundColor" "black"
                                                      "color"           "white"})))))})))})

               (cond (or value-entity-empty?
                         empty-value?)
                     {:placeholder (str (:placeholder props))}

                     has-focus?
                     {:placeholder (str (or (:label value-option)
                                            (:placeholder props)))})
               {:on-key-down
                (fn [e & params-js]
                  (let [key-code (->> e
                                      .-keyCode)]
                    (when (some #{key-code} [9 13 27 40 38])
                      (let [highlighted-index (:highlighted-index @select-atom)
                            ordered-options (:all-options @select-atom)]
                        (case key-code
                          ;; ENTER
                          13
                          (let [highlighted-option (when (and (> highlighted-index -1)
                                                              ordered-options
                                                              (coll? ordered-options)
                                                              (> (count ordered-options) 0)
                                                              (< highlighted-index
                                                                 (count ordered-options)))
                                                     (nth ordered-options highlighted-index))]
                            (if (:new-value highlighted-option)
                              (let [on-create-option (:on-create-option props)]
                                (on-create-option (:new-value highlighted-option))
                                (swap! select-atom dissoc :menu-is-open?))
                              (do
                                (let [select! (:highlight-select! @select-atom)]
                                  (when (fn? select!)
                                    (select! highlighted-index)))
                                (.preventDefault e)
                                (.stopPropagation e)
                                (swap! select-atom dissoc :menu-is-open?)))
                            (swap! select-atom dissoc :temp-input)
                            (swap! select-atom assoc :has-focus? false)
                            (deref select-atom)
                            (js/setTimeout (fn [] (fu/blur-active-element!)) 4))
                          ;; TAB
                          9 (do (swap! select-atom assoc :tabbed? true)
                                (swap! select-atom dissoc :menu-is-open?)
                                (deref select-atom)
                                (.preventDefault e)
                                (.stopPropagation e)
                                (js/setTimeout (fn [] (fu/blur-active-element!)) 4))
                          ;; ARROW DOWN
                          40 (when (and ordered-options
                                        (< highlighted-index (dec (count ordered-options))))
                               (swap! select-atom assoc
                                      :highlighted-index
                                      (inc highlighted-index)))
                          ;; ARROW UP
                          38 (when (and ordered-options
                                        (> highlighted-index 0))
                               (swap! select-atom assoc
                                      :highlighted-index
                                      (dec highlighted-index)))
                          ;; ESC
                          27 (do (swap! select-atom assoc :has-focus? false)
                                 (deref select-atom)
                                 (fu/blur-active-element!))
                          nil)))))
                :on-input-change (fn [v js-params]
                                   (let [params (js->clj js-params :keywordize-keys true)
                                         action (:action params)
                                         timeout-handle (:timeout-handle @timeout-atom)]
                                     (when timeout-handle
                                       (js/clearTimeout timeout-handle))

                                     (case action
                                       "input-change" (do
                                                        (swap! select-atom assoc :temp-input v))
                                       "set-value" (do)
                                       "input-blur" ""
                                       "menu-close" (do)
                                       (logging/error
                                        "Unexpected js/Select.Async/on-input-change action "
                                        action))

                                     (let [new-timeout-handle (js/setTimeout (fn []
                                                                               (recalculate-options
                                                                                identity))
                                                                             400)]
                                       (swap! timeout-atom assoc :timeout-handle new-timeout-handle))
                                     nil))}
               (dissoc
                props
                :value
                :on-create-option
                :read-only?
                :placeholder
                :hide-dropdown-indicator?
                :style-override)
               #_{:menu-is-open true})
              final-args
              (potpuri/deep-merge
               {:components (merge
                             {:Option (r/reactify-component (partial render-option value))
                              :Menu   menu-component}
                             (when multi-selectable?
                               {:MultiValue          (r/reactify-component (fn [_]))
                                :MultiValueContainer (r/reactify-component
                                                      (fn [_]))})
                             (when hide-dropdown-indicator?
                               {:DropdownIndicator  (r/reactify-component (fn [_]))
                                :IndicatorSeparator (r/reactify-component (fn [_]))}))}
               component-args)]
          (if creatable?
            [:> Async-Select-Creatable final-args]
            [:> Async-Select final-args])))})))

(defn select
  [props]
  [create-select {:props (merge props)}])


(defonce dbg (atom {}))
(comment
 (-> dbg
     deref
     :oppo/target-asset
     (select-keys [:value :entity-types])))
(defn select-creatable
  [{:keys [entity-types creatable-entity-types value-type attribute prompt-text-creator-keyword]
    :as   props}]
  (swap! dbg assoc attribute props)
  (let [tenant-schema @session/tenant-schema]
    [create-select
     {:creatable? true
      :props      (merge
                   {:create-option-position "first"
                    :get-new-option-data
                    (fn [args]
                      (let
                       [create-label
                        (cond
                          prompt-text-creator-keyword
                          (loc/tr prompt-text-creator-keyword
                            [args])
                          (or (not creatable-entity-types)
                              (empty? creatable-entity-types))
                          (loc/tr :create-new-option)
                          :else
                          (if (cu/one-element? creatable-entity-types)
                            (let [entity-type (first creatable-entity-types)]
                              (loc/tr
                                :search-create-option-new
                                [(if (= entity-type :entity.type/named-value)
                                   (or (when-let [fs (registry/get-field-schema
                                                      tenant-schema
                                                      attribute)]
                                         (loc/field-name attribute fs))
                                       (loc/tr attribute))
                                   (loc/tr entity-type))
                                 args]))
                            (loc/tr
                              :search-create-option-new-one-of-many-entity-types
                              [args])))]
                        {:new-value  (cu/str-trim args)
                         :icon       (fn [data]
                                       [:i.fal.fa-plus-circle.create-option__icon])
                         :class-name "create-option select-dropdown-menu-button"
                         :label      create-label}))}
                   props)}]))
